import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { stripDemoVisitors } from '@/lib/demo-data';
import { createStableId, readJsonStorage, storageKeys, writeJsonStorage } from '@/lib/storage';
import type {
  CustomerStatus,
  FeedbackDeliveryStatus,
  SessionRole,
  StaffMember,
  VisitorEntry,
} from '@/lib/types';
import { useStaffDirectoryState } from './StaffContext';
import { useSessionState } from './SessionContext';

type VisitorDraft = {
  visitorName: string;
  visitorPhone: string;
  visitFrequency?: string | null;
  services: string[];
  staffId: StaffMember['id'];
  totalAmount: number;
  createdByRole: SessionRole;
  createdByName: string;
};

type VisitorUpdate = Partial<
  Pick<
    VisitorEntry,
    | 'feedbackSentAt'
    | 'feedbackDeliveryStatus'
    | 'feedbackRespondedAt'
    | 'feedbackRating'
    | 'feedbackNote'
    | 'feedbackVisibleToClient'
    | 'visitFrequency'
    | 'customerStatusOverride'
  >
>;

type VisitorContextValue = {
  isReady: boolean;
  visitors: VisitorEntry[];
  registerVisitor: (draft: VisitorDraft) => Promise<VisitorEntry>;
  updateVisitor: (visitorId: string, updates: VisitorUpdate) => Promise<VisitorEntry | null>;
  refreshVisitors: () => Promise<void>;
};

const VisitorContext = createContext<VisitorContextValue | null>(null);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSessionRole(value: unknown): value is SessionRole {
  return value === 'enterprise' || value === 'staff';
}

function isCustomerStatus(value: unknown): value is CustomerStatus {
  return (
    value === 'new' ||
    value === 'follow_up' ||
    value === 'not_answering' ||
    value === 'irrelevant' ||
    value === 'junk'
  );
}

function isFeedbackDeliveryStatus(value: unknown): value is FeedbackDeliveryStatus {
  return value === 'pending' || value === 'sent' || value === 'failed';
}

function isVisitorEntry(value: unknown): value is VisitorEntry {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.visitorName === 'string' &&
    typeof value.visitorPhone === 'string' &&
    (value.visitFrequency === undefined || value.visitFrequency === null || typeof value.visitFrequency === 'string') &&
    Array.isArray(value.services) &&
    value.services.every((service) => typeof service === 'string') &&
    typeof value.staffId === 'string' &&
    typeof value.staffName === 'string' &&
    typeof value.totalAmount === 'number' &&
    typeof value.createdAt === 'string' &&
    isSessionRole(value.createdByRole) &&
    typeof value.createdByName === 'string' &&
    (value.feedbackSentAt === undefined || value.feedbackSentAt === null || typeof value.feedbackSentAt === 'string') &&
    (value.feedbackDeliveryStatus === undefined || isFeedbackDeliveryStatus(value.feedbackDeliveryStatus)) &&
    (value.feedbackRespondedAt === undefined ||
      value.feedbackRespondedAt === null ||
      typeof value.feedbackRespondedAt === 'string') &&
    (value.feedbackRating === undefined || value.feedbackRating === null || typeof value.feedbackRating === 'number') &&
    (value.feedbackNote === undefined || value.feedbackNote === null || typeof value.feedbackNote === 'string') &&
    (value.feedbackVisibleToClient === undefined || typeof value.feedbackVisibleToClient === 'boolean') &&
    (value.customerStatusOverride === undefined ||
      value.customerStatusOverride === null ||
      isCustomerStatus(value.customerStatusOverride))
  );
}

function sortVisitors(visitors: VisitorEntry[]) {
  return [...visitors].sort((left, right) => {
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

function normalizeVisitors(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return sortVisitors(value.filter(isVisitorEntry));
}

export function VisitorProvider({ children }: { children: ReactNode }) {
  const { staffMembers } = useStaffDirectoryState();
  const { sessionName, sessionRole, sessionStaffId, sessionUsername } = useSessionState();
  const [visitors, setVisitors] = useState<VisitorEntry[]>([]);
  const [isReady, setIsReady] = useState(false);
  const loadedStorageKeyRef = useRef<string | null>(null);

  const visitorStorageKey = useMemo(() => {
    if (sessionRole === 'staff') {
      const identity = sessionStaffId ?? sessionUsername ?? 'current-staff';
      return `${storageKeys.visitors}:staff:${identity}`;
    }

    return storageKeys.visitors;
  }, [sessionRole, sessionStaffId, sessionUsername]);

  const refreshVisitors = useCallback(async () => {
    const stored = readJsonStorage<VisitorEntry[]>(visitorStorageKey);

    if (!stored && sessionRole === 'staff' && sessionStaffId) {
      const legacyVisitors = stripDemoVisitors(
        normalizeVisitors(readJsonStorage<VisitorEntry[]>(storageKeys.visitors))
      ).filter((visitor) => visitor.staffId === sessionStaffId);

      loadedStorageKeyRef.current = visitorStorageKey;
      setVisitors(legacyVisitors);
      writeJsonStorage(visitorStorageKey, legacyVisitors);
      return;
    }

    const cleaned = stripDemoVisitors(normalizeVisitors(stored));
    loadedStorageKeyRef.current = visitorStorageKey;
    setVisitors(cleaned);
    writeJsonStorage(visitorStorageKey, cleaned);
  }, [sessionRole, sessionStaffId, visitorStorageKey]);

  useEffect(() => {
    let active = true;

    loadedStorageKeyRef.current = null;
    setIsReady(false);
    setVisitors([]);

    (async () => {
      await refreshVisitors();

      if (active) {
        setIsReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [refreshVisitors]);

  useEffect(() => {
    if (!isReady || loadedStorageKeyRef.current !== visitorStorageKey) {
      return;
    }

    writeJsonStorage(visitorStorageKey, visitors);

    if (sessionRole === 'staff') {
      const staffIdentity = sessionStaffId ?? sessionUsername ?? 'current-staff';
      const globalVisitors = stripDemoVisitors(
        normalizeVisitors(readJsonStorage<VisitorEntry[]>(storageKeys.visitors))
      );
      const otherVisitors = globalVisitors.filter(
        (visitor) => visitor.staffId !== staffIdentity
      );
      writeJsonStorage(
        storageKeys.visitors,
        sortVisitors([...visitors, ...otherVisitors])
      );
    }
  }, [isReady, sessionRole, sessionStaffId, sessionUsername, visitorStorageKey, visitors]);

  const registerVisitor = useCallback(async (draft: VisitorDraft) => {
    const isStaffAccount = sessionRole === 'staff';
    const authenticatedStaff = isStaffAccount
      ? staffMembers.find((member) => member.id === sessionStaffId) ??
        staffMembers.find(
          (member) => Boolean(sessionUsername) && member.username === sessionUsername
        )
      : null;
    const staff = isStaffAccount
      ? authenticatedStaff
      : staffMembers.find((member) => member.id === draft.staffId);

    if (!staff && !isStaffAccount && draft.createdByRole !== 'enterprise') {
      throw new Error('Unknown staff member for this visit.');
    }

    const fallbackStaffId = isStaffAccount
      ? sessionStaffId ?? sessionUsername ?? 'current-staff'
      : draft.staffId.trim() || 'admin';
    const fallbackStaffName = isStaffAccount
      ? sessionName?.trim() || 'Staff'
      : draft.createdByName.trim() || 'Admin';
    const resolvedStaff = staff ?? {
      id: fallbackStaffId,
      name: fallbackStaffName,
    };

    const services = draft.services.map((service) => service.trim()).filter(Boolean);

    if (!draft.visitorName.trim()) {
      throw new Error('Visitor name is required.');
    }

    if (!draft.visitorPhone.trim()) {
      throw new Error('Visitor phone number is required.');
    }

    if (services.length === 0) {
      throw new Error('Select at least one service.');
    }

    if (!Number.isFinite(draft.totalAmount) || draft.totalAmount <= 0) {
      throw new Error('Enter a valid total amount.');
    }

    const nextVisitor: VisitorEntry = {
      id: createStableId('visitor'),
      visitorName: draft.visitorName.trim(),
      visitorPhone: draft.visitorPhone.trim(),
      visitFrequency: draft.visitFrequency?.trim() || null,
      services,
      staffId: resolvedStaff.id,
      staffName: resolvedStaff.name,
      totalAmount: Math.round(draft.totalAmount),
      createdAt: new Date().toISOString(),
      createdByRole: isStaffAccount ? 'staff' : draft.createdByRole,
      createdByName: isStaffAccount
        ? sessionName?.trim() || resolvedStaff.name
        : draft.createdByName.trim() || 'Admin',
      feedbackSentAt: new Date().toISOString(),
      feedbackDeliveryStatus: 'sent',
      feedbackRespondedAt: null,
      feedbackRating: null,
      feedbackNote: null,
      feedbackVisibleToClient: true,
      customerStatusOverride: null,
    };

    setVisitors((current) => sortVisitors([nextVisitor, ...current]));

    return nextVisitor;
  }, [sessionName, sessionRole, sessionStaffId, sessionUsername, staffMembers]);

  const updateVisitor = useCallback(async (visitorId: string, updates: VisitorUpdate) => {
    let updatedVisitor: VisitorEntry | null = null;

    setVisitors((current) =>
      sortVisitors(
        current.map((visitor) => {
          if (visitor.id !== visitorId) {
            return visitor;
          }

          if (sessionRole === 'staff' && sessionStaffId && visitor.staffId !== sessionStaffId) {
            return visitor;
          }

          updatedVisitor = {
            ...visitor,
            ...updates,
          };

          return updatedVisitor;
        })
      )
    );

    if (!updatedVisitor) {
      return null;
    }

    return updatedVisitor;
  }, [sessionRole, sessionStaffId]);

  const value = useMemo(
    () => ({
      isReady,
      visitors,
      registerVisitor,
      updateVisitor,
      refreshVisitors,
    }),
    [isReady, refreshVisitors, registerVisitor, updateVisitor, visitors]
  );

  return <VisitorContext.Provider value={value}>{children}</VisitorContext.Provider>;
}

export function useVisitorState() {
  const context = useContext(VisitorContext);

  if (!context) {
    throw new Error('useVisitorState must be used within a VisitorProvider');
  }

  return context;
}

export type { VisitorDraft, VisitorUpdate };
